import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { OutreachContactKind, OutreachWorkspaceContactStatus } from 'fym-shared/src/music24/outreach/types';
import { useTRPCAPI } from '../../../../trpc/trpc';
import type { ContactRow, ContactsPage } from '../outreachTypes';
import { useRetry } from './useRetry';

export interface ContactFilters {
  listUuid: string | null;
  status: OutreachWorkspaceContactStatus | null;
  q: string;
  tag: string | null;
  /** "Your imports" (design outreach-v2 k1). */
  source?: 'imported' | null;
  kind?: OutreachContactKind | null;
}

export type ContactCounts = ContactsPage['counts'];

const NO_COUNTS: ContactCounts = { all: 0, replied: 0, placed: 0, imported: 0, doNotContact: 0 };

/** `outreach.contacts.list` (design outreach-v2 k1): 50 rows a page and the chip counts. */
export function useContacts(accountId: number, filters: ContactFilters) {
  const trpcAPI = useTRPCAPI();
  const query = useInfiniteQuery(
    trpcAPI.outreach.contacts.list.infiniteQueryOptions(
      {
        accountId,
        listUuid: filters.listUuid,
        status: filters.status,
        q: filters.q.trim() === '' ? null : filters.q.trim(),
        tag: filters.tag,
        source: filters.source ?? null,
        kind: filters.kind ?? null,
      },
      { getNextPageParam: (page: ContactsPage) => page.nextCursor ?? undefined, retry: false }
    )
  );
  const pages = query.data?.pages as ContactsPage[] | undefined;
  const rows = useMemo<ContactRow[]>(() => (pages ?? []).flatMap((page) => page.rows), [pages]);
  const retry = useRetry(query.refetch);
  return {
    rows,
    total: pages?.[0]?.total ?? 0,
    counts: pages?.[0]?.counts ?? NO_COUNTS,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
}
